import type { TPSDocument, TPSOrderInfo } from '@/company-portal/add-file/add-file.types';
import type {
    Client,
    ClientOption,
    DocumentActionRoutes,
    DocumentBinaryUploadConfig,
    DocumentContentOrReference,
    DocumentErrorIndex,
    DocumentTemplateUploadConfig,
    DocumentTpsUploadConfig,
    DocumentUploadConfig,
    DocumentUploadError,
    DocumentUploadErrorResponse,
    DocumentUploadPayload,
    ESignEditRequest,
    FileDetailsDocumentRoutes,
    IndividualDocumentErrors,
    MergedDocumentErrorMap,
    TPSDocumentVendorContext,
} from '@/components/Views/Company/File/file.types';
import {
    DocumentUploadMergedErrorKeys,
    DocumentUploadValidationErrorKeys,
    ESignEditSource,
} from '@/components/Views/Company/File/file.types';
import type { DocumentTemplate } from '@/interfaces/document-management/document-management.types';
import { TEMPLATE_CONVENTION_PREFIX } from '@/interfaces/document-management/document-management.types';
import { ensureArray } from '@/shared/utils/array-utils';
import { joinFirstLast } from '@/shared/utils/strings';
import type { AddDocumentParams } from '@/stores/file-document.store';

export type ClientOptionInput = Pick<Client, 'id' | 'first' | 'last' | 'email' | 'role'>;

export const DOCUMENTS_RECIPIENTS_ERROR = 'Add one or more clients';
export const DOCUMENTS_SIGNERS_ERROR = 'Add one or more signers';

export const getClientOptions = (clients: ClientOptionInput[]): ClientOption[] => {
    return clients.map(client => ({
        id: client.id.toString(),
        value: client.id,
        label: `${client.first} ${client.last} (${client.email})`,
        email: client.email,
        role: client.role,
        first: client.first,
        last: client.last,
        tooltipText: client.email,
    }));
};

export type DocumentTemplateOption = {
    id: DocumentTemplate['id'];
    value: DocumentTemplate['id'];
    label: DocumentTemplate['name'];
    signersCount: number;
};

const formatTemplateName = (name: string): string => {
    return name.replace(new RegExp(`^${TEMPLATE_CONVENTION_PREFIX}`, 'i'), '').trim();
};

export const getTemplateOptions = (templates?: DocumentTemplate[]) => {
    return (
        templates
            ?.filter(t => !!t.name)
            .map(template => ({
                id: template.id,
                value: template.id,
                label: formatTemplateName(template.name),
                signersCount: template.signers?.filter(s => s.type === 'signer').length ?? 0,
            })) ?? []
    );
};

/**
 * Gets the display content for a client chip/badge.
 * Returns the client's full name if available, otherwise falls back to email.
 */
export const getClientChipContent = (client: Pick<ClientOption, 'first' | 'last' | 'email'>): string => {
    const name = joinFirstLast(client.first, client.last);
    return name || client.email;
};

/**
 * Generates an indexed error key for accessing backend validation errors.
 * Backend returns errors keyed by whatever value the FormData array key was — typically
 * `documents.0`, `documents_recipients.1` for binary uploads, or `documents.<tpsId>` for
 * TPS vendor documents.
 */
export const getIndexedErrorKey = (
    validationKey: DocumentUploadValidationErrorKeys,
    documentErrorIndex: DocumentErrorIndex,
): keyof DocumentUploadError => {
    return `${validationKey}.${documentErrorIndex}`;
};

export const isDocumentTemplateConfig = (config: Partial<DocumentUploadConfig> = {}): config is DocumentTemplateUploadConfig => {
    return 'template' in config && !!config.template?.id;
};

export const isDocumentBinaryConfig = (config: Partial<DocumentUploadConfig> = {}): config is DocumentBinaryUploadConfig => {
    return 'file' in config && !!config.file?.size;
};

export const isDocumentTpsConfig = (config: Partial<DocumentUploadConfig> = {}): config is DocumentTpsUploadConfig => {
    return 'file_temp_path' in config && !!config.file_temp_path;
};

/**
 * Every config carries a stable identity in `documentId`:
 * - binary uploads: UUID generated by `DsUpload` at file-stage time
 * - templates: the Docusign template UUID
 * - TPS docs: the vendor's document id (`tpsDocument.id`)
 * BE supports any string as the form key
 */
export const getDocumentFormKey = (config: DocumentUploadConfig): string => config.formKey;

/**
 * Maps document-specific errors from the backend error response structure to a normalized format.
 * Handles both array and single-string error values by ensuring they're always arrays.
 */
export const mapDocumentErrors = (
    errorResponse: DocumentUploadErrorResponse,
    documentErrorIndex: DocumentErrorIndex,
): IndividualDocumentErrors => {
    const { DOCUMENT, DOCUMENTS_CONFIG, DOCUMENT_RECIPIENTS, DOCUMENT_SIGNERS, DOCUMENT_SIGN_GENERAL } =
        DocumentUploadValidationErrorKeys;
    const validationErrors = errorResponse.errors ?? {};

    const generalDocumentError = ensureArray(validationErrors[getIndexedErrorKey(DOCUMENT, documentErrorIndex)]);
    const generalDocumentConfigError = ensureArray(validationErrors[getIndexedErrorKey(DOCUMENTS_CONFIG, documentErrorIndex)]);

    return {
        [DOCUMENT]: [...generalDocumentError, ...generalDocumentConfigError],
        [DOCUMENT_RECIPIENTS]: ensureArray(validationErrors[getIndexedErrorKey(DOCUMENT_RECIPIENTS, documentErrorIndex)]),

        [DOCUMENT_SIGNERS]: ensureArray(validationErrors[getIndexedErrorKey(DOCUMENT_SIGNERS, documentErrorIndex)]),
        [DOCUMENT_SIGN_GENERAL]: ensureArray(validationErrors[getIndexedErrorKey(DOCUMENT_SIGN_GENERAL, documentErrorIndex)]),
    };
};

/**
 * Merges individual error arrays into a Map with user-friendly error messages.
 * Joins multiple error messages.
 */
export const mergeErrors = (errors: Partial<IndividualDocumentErrors>): MergedDocumentErrorMap => {
    const { ALL_SIGNERS_ERROR, DOCUMENT_LEVEL_ERROR, RECIPIENTS_ERROR, SIGNERS_ERROR, SIGN_CHECKBOX_ERROR } =
        DocumentUploadMergedErrorKeys;
    const { DOCUMENT, DOCUMENT_RECIPIENTS, DOCUMENT_SIGNERS, DOCUMENT_SIGN_GENERAL } = DocumentUploadValidationErrorKeys;

    const joinMessages = (messages?: string[]) => messages?.filter(Boolean)?.join('. ') ?? '';

    const documentSignersError = joinMessages(errors[DOCUMENT_SIGNERS]);
    const generalEsignErrors = joinMessages(errors[DOCUMENT_SIGN_GENERAL]);

    return new Map<DocumentUploadMergedErrorKeys, string>([
        [DOCUMENT_LEVEL_ERROR, joinMessages(errors[DOCUMENT])],
        [RECIPIENTS_ERROR, joinMessages(errors[DOCUMENT_RECIPIENTS])],
        [SIGNERS_ERROR, documentSignersError],
        [SIGN_CHECKBOX_ERROR, generalEsignErrors],
        [ALL_SIGNERS_ERROR, joinMessages([documentSignersError, generalEsignErrors])],
    ]);
};

/**
 * V1: Builds FormData for document upload requests.
 * Supports binary uploads and TPS vendor documents.
 */
export const buildDocumentUploadFormDataV1 = (payload: DocumentUploadPayload<DocumentUploadConfig>): FormData => {
    const formData = new FormData();

    payload.documents.forEach(config => {
        const id = getDocumentFormKey(config);

        const document = resolveDocumentForRequest(config);
        if (!document) {
            return;
        }

        formData.append(`documents[${id}]`, document);

        config.recipients.forEach(recipientId => {
            formData.append(`documents_recipients[${id}][]`, recipientId.toString());
        });

        formData.append(`documents_sign_edit[${id}]`, config.esignEditId || '');

        if (config.needsEsignature) {
            // BE rule `documents_sign.*` validates the value as `integer`; pass the iteration
            // index so the rule passes regardless of whether `id` is numeric (TPS) or a UUID.
            // The BE only checks `!== null` on the value, so its specific number doesn't matter.
            formData.append(`documents_sign[${id}]`, `${id}`);

            config.signers.forEach((signerId, signerIdx) => {
                formData.append(`documents_signers[${id}][${signerIdx}]`, signerId.toString());
            });
        }
    });

    if (payload.sendNotification) {
        formData.append('notify', 'notify');
    }

    return formData;
};

/** Type for the stringified object sent in a FormData object */
interface V2DocumentSerializedConfig {
    recipients: number[];
    signer_ids?: number[];
    template_id?: string;
    name?: string;
    upload_key?: number;
    folder_id?: string;
}

/**
 * V2: Builds FormData for document upload requests (supports templates + uploads).
 * Uses JSON-based documents_config structure with separate uploads[] array.
 */
export const buildDocumentUploadFormDataV2 = (payload: DocumentUploadPayload, csrfToken: string): FormData => {
    const formData = new FormData();

    formData.append('_method', 'PATCH');
    formData.append('_token', csrfToken);

    const documentsConfig: V2DocumentSerializedConfig[] = [];

    payload.documents.forEach((config, index) => {
        const commonConfig = {
            recipients: config.recipients,
            signer_ids: config.needsEsignature ? config.signers : undefined,
            folder_id: config.esignEditId,
        };

        if (isDocumentBinaryConfig(config)) {
            // The name 'uploads[$index]` is a PHP convention for multi-part forms. Don't change this
            // name or the filename, unless we change the backend to something else.
            formData.append(`uploads[${index}]`, config.file, `${config.file.name}`);
            documentsConfig.push({
                ...commonConfig,
                name: config.file.name,
                upload_key: index,
            });
        } else if (isDocumentTemplateConfig(config)) {
            documentsConfig.push({
                ...commonConfig,
                template_id: config.template?.id,
            });
        }
    });

    const configPayload = {
        documents: documentsConfig,
        notify: payload.sendNotification ? 'notify' : undefined,
    };

    formData.append('documents_config', JSON.stringify(configPayload));

    return formData;
};

/**
 * V1: Builds FormData for eSign edit URL requests (file uploads only).
 * Handles signer information and optional existing folder ID for re-editing.
 */
export const buildESignEditFormDataV1 = (request: ESignEditRequest, csrfToken: string): FormData => {
    const formData = new FormData();
    formData.append('_method', 'POST');
    formData.append('_token', csrfToken);
    formData.append('source', request.source);
    if (request.document) {
        formData.append(`documents[${request.documentFormKey}]`, request.document);
    }
    formData.append('branch_id', request.branchId.toString());
    if (request.vendorInfo) {
        formData.append('vendor', request.vendorInfo.vendor);
        formData.append('vendor_order_id', request.vendorInfo.vendor_order_id);
        formData.append('vendor_transaction_id', request.vendorInfo.vendor_transaction_id);
        formData.append('source', ESignEditSource.VENDOR);
    }

    // Add signers
    request.signerIds.forEach(id => {
        formData.append('signers[]', id.toString());
    });

    if (request.signerEmails) {
        Object.entries(request.signerEmails).forEach(([signerId, email]) => {
            formData.append(`email[${signerId}]`, email);
        });
    }

    // Add existing folder ID if re-editing so changes are not lost
    if (request.existingFolderId) {
        formData.append(`documents_sign_edit[${request.documentFormKey}]`, request.existingFolderId);
    }

    return formData;
};

/**
 * V2: Builds FormData for eSign edit URL requests (supports templates)
 */
export const buildESignEditFormDataV2 = (request: ESignEditRequest, csrfToken: string): FormData => {
    const formData = new FormData();
    formData.append('_method', 'POST');
    formData.append('_token', csrfToken);

    const signaturesConfig: Record<string, unknown> = {
        branch_id: request.branchId,
        source: request.source,
        template_id: request.templateId,
    };

    if (request.templateId) {
        signaturesConfig.template_id = request.templateId;
    }

    if (request.source === ESignEditSource.WEB_UPDATE) {
        signaturesConfig.signer_ids = request.signerIds;
    } else if (request.signerEmails) {
        // Map signerIds in selection order to preserve user-selected signer sequence.
        // Object.values() would sort by numeric key (client ID), not selection order.
        signaturesConfig.signer_emails = request.signerIds.map(id => request.signerEmails![id]);
    }

    formData.append('signatures_config', JSON.stringify(signaturesConfig));
    return formData;
};

export const buildESignEditFormData = (request: ESignEditRequest, csrfToken: string): FormData => {
    return request.templateId ? buildESignEditFormDataV2(request, csrfToken) : buildESignEditFormDataV1(request, csrfToken);
};

export const validateSignersSelected = (doc: DocumentUploadConfig): string[] => {
    if (doc.needsEsignature && doc.signers.length === 0) {
        return [DOCUMENTS_SIGNERS_ERROR];
    }
    return [];
};

export const validateRecipientsSelected = (doc: DocumentUploadConfig): string[] => {
    if (doc.recipients.length === 0) {
        return [DOCUMENTS_RECIPIENTS_ERROR];
    }
    return [];
};

export const validateDocumentSetup = (
    docs: DocumentUploadConfig[],
): { isValid: boolean; errors: { documentId: string; errors: Partial<IndividualDocumentErrors> }[] } => {
    let isValid = true;
    const errors: { documentId: string; errors: Partial<IndividualDocumentErrors> }[] = [];
    docs.forEach(doc => {
        if (!doc.isSelected) return;
        /**
         * Important validation note: we can't safely require that the user used the
         * edit/review esign action because some documents are prepared with
         * signing field notation in the content, making that procedure optional.
         *
         * A Backend level validation checks for the match of signers with signing fields
         */
        const errorsSigners = validateSignersSelected(doc);
        const errorsRecipients = validateRecipientsSelected(doc);
        if (errorsSigners.length > 0 || errorsRecipients.length > 0) {
            errors.push({
                documentId: doc.documentId,
                errors: {
                    [DocumentUploadValidationErrorKeys.DOCUMENT_RECIPIENTS]: errorsRecipients,
                    [DocumentUploadValidationErrorKeys.DOCUMENT_SIGNERS]: errorsSigners,
                },
            });
            isValid = false;
        }
    });

    return { isValid, errors };
};

export const routesSupportEsignV2 = (routes?: DocumentActionRoutes): routes is FileDetailsDocumentRoutes => {
    return !!routes && 'esign_edit_v2' in routes;
};

export const resolveDocumentForRequest = (config: DocumentUploadConfig): DocumentContentOrReference | undefined => {
    if (isDocumentBinaryConfig(config)) {
        return config.file;
    }
    if (isDocumentTemplateConfig(config)) {
        return config.template.id;
    }
    if (isDocumentTpsConfig(config)) {
        return config.file_temp_path;
    }
};

export const routesSupportAddDocV2 = (routes?: DocumentActionRoutes): routes is FileDetailsDocumentRoutes => {
    return (
        !!routes && 'add_document' in routes && 'add_document_v2' in routes && !!routes.add_document && !!routes.add_document_v2
    );
};

// TPS
export const getVendorContext = (tpsOrder: TPSOrderInfo): TPSDocumentVendorContext => ({
    vendor_order_id: tpsOrder.vendor_order_id,
    vendor_transaction_id: tpsOrder.vendor_transaction_id,
    vendor: tpsOrder.vendor,
});

export const buildAddTpsDocumentParams = (tpsDocument: TPSDocument, tpsOrder: TPSOrderInfo): AddDocumentParams => ({
    documentId: `${tpsDocument.id}`,
    tpsDocument: {
        ...tpsDocument,
        ...getVendorContext(tpsOrder),
    },
    isSelected: tpsOrder.autoSelectDocuments ?? true, // forward autoSelectDocuments property from tpsOrderInfo, fall back to true to match legacy implementation
});
